import { storedInvitation } from '../invitation';
import type { InboxEntry } from '@alexiarest/shared';
import type {
  PurchaseDocumentType,
  Draft,
  PosIntegrationPatch,
  PosIntegrationsResponse,
  PosIntegrationView,
  PosProvider,
  PosSyncResult,
  PosUnitsResult,
  AdminOverview,
  PlatformExpense,
  PlatformRecurringExpense,
  PlatformSettings,
  PlatformExpenseInput,
  PlatformPnlResponse,
  CashMovementInput,
  DailyRevenueInput,
  ExpenseInput,
  IngredientInput,
  InvestmentInput,
  InventoryCount,
  InventoryCountInput,
  MenuItemInput,
  Invoice,
  Period,
  RecipeInput,
  TargetInput,
  TenantSettings,
  BillingInterval,
} from '@alexiarest/shared';
import { getToken, refreshSession } from '../auth/token';
import type {
  AlexiaRestApi,
  BatchResult,
  ConfirmRecipeProposalInput,
  ConfirmRecipeProposalResponse,
  IngredientBatch,
  OnboardResponse,
  RecipeBatch,
  RecipeProposalResponse,
  RecipeAudioInput,
  PhotoUploadTicket,
  InventoryScanResult,
  ScanQuotaResponse,
  ScanResult,
  SupplierInput,
  HoldedReintento,
} from './contract';
import { demoApi } from './demo';
import { ApiError, fallbackMessage, type ApiErrorCode } from './error';
import type {
  AmortizationRow,
  AssignableRole,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantSuggestion,
  AssistantToolCall,
  CashFlowResponse,
  CashMovement,
  CostDeviation,
  CustomRole,
  CustomRoleInput,
  DailyRevenue,
  DashboardResponse,
  ExpenseEntry,
  Ingredient,
  InviteInput,
  Investment,
  KpisResponse,
  Location,
  MapeoVentasPropuesta,
  MeResponse,
  Membership,
  MenuItem,
  PayablesResponse,
  PricingAnalysis,
  PricingResponse,
  ProfitAndLoss,
  Recipe,
  RecipeCost,
  RecipeCostsResponse,
  ReconciliationResult,
  Supplier,
  Target,
  TargetComparison,
  TaxAllocation,
  TaxAllocationRequest,
  TeamResponse,
  Tenant,
  TenantUpdate,
  WeekdayPerformance,
} from './types';

const RAW_BASE = (import.meta.env.VITE_API_URL ?? '').trim();

/**
 * Si no hay `VITE_API_URL` la app funciona entera en el navegador con el
 * dataset de demostracion: nada de red, nada de backend.
 */
export const isDemoMode = RAW_BASE === '';

const BASE_URL = RAW_BASE.replace(/\/+$/, '');

const KNOWN_CODES: ApiErrorCode[] = [
  'VALIDATION_ERROR',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'PLAN_LIMIT',
  'PAYLOAD_TOO_LARGE',
  'NOT_CONFIGURED',
  'INTERNAL',
];

function codeFromStatus(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'VALIDATION_ERROR';
    case 401:
      return 'UNAUTHORIZED';
    case 402:
      return 'PLAN_LIMIT';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    default:
      return 'INTERNAL';
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

/** 502/503/504: la API no ha llegado a contestar (timeout o caida). */
const esCorteDeLaPasarela = (status: number): boolean =>
  status === 502 || status === 503 || status === 504;

/** Traduce la respuesta de error del contrato a un `ApiError`. */
async function toApiError(response: Response): Promise<ApiError> {
  const fallbackCode = codeFromStatus(response.status);
  let body: ErrorBody | null = null;
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    body = null;
  }

  const rawCode = body?.error?.code;
  const code = KNOWN_CODES.includes(rawCode as ApiErrorCode)
    ? (rawCode as ApiErrorCode)
    : fallbackCode;
  /*
   * Sin cuerpo con nuestro formato, el fallo no viene de la API sino de la
   * puerta de enlace: la Lambda se ha quedado sin tiempo o se ha caido, y AWS
   * ha respondido por su cuenta. Es un caso distinto y merece un mensaje
   * distinto, porque "error inesperado, prueba otra vez" con una pregunta que
   * tarda de mas hace que el usuario reintente lo mismo y vuelva a fallar.
   */
  const message =
    body?.error?.message?.trim() ||
    (body === null && esCorteDeLaPasarela(response.status)
      ? 'La consulta ha tardado demasiado y se ha cortado. Si has preguntado por varias cosas a la vez, prueba a preguntarlas de una en una.'
      : fallbackMessage(code));
  const details = Array.isArray(body?.error?.details)
    ? (body.error.details as ApiError['details'])
    : [];

  return new ApiError(code, message, response.status, details);
}

async function doFetch(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');

  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  try {
    return await fetch(`${BASE_URL}${path}`, { ...init, headers });
  } catch {
    throw new ApiError('NETWORK', fallbackMessage('NETWORK'));
  }
}

/**
 * Reintentos con espera creciente (lanzamiento a los Founders, 5 sep 2026).
 *
 * La cuenta de AWS tiene la concurrencia de Lambda muy limitada mientras se
 * aprueba la ampliacion: cuando varias personas cargan a la vez, la puerta
 * de enlace contesta 429 o un 5xx SIN el formato de nuestra API, porque la
 * funcion ni ha llegado a ejecutarse. En lecturas se reintenta cualquier
 * 5xx; en escrituras solo el 429: un 502/504 puede ser un guardado que
 * termino tarde, y repetirlo lo duplica.
 */
const REINTENTOS_MS = [400, 1200, 2500];

const esLectura = (init: RequestInit): boolean => {
  const metodo = (init.method ?? 'GET').toUpperCase();
  return metodo === 'GET' || metodo === 'HEAD';
};

async function esFalloDePasarela(response: Response): Promise<boolean> {
  if (response.status === 429 || esCorteDeLaPasarela(response.status)) return true;
  if (response.status !== 500) return false;
  try {
    const body = (await response.clone().json()) as ErrorBody;
    return !body?.error;
  } catch {
    return true;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response = await doFetch(path, init);

  // Un 401 con refresh token a mano no es "sesion caducada": se renueva y se
  // reintenta UNA vez. refreshSession() colapsa renovaciones concurrentes.
  if (response.status === 401 && (await refreshSession())) {
    response = await doFetch(path, init);
  }

  for (const espera of REINTENTOS_MS) {
    if (response.ok) break;
    /*
     * Escrituras: SOLO el 429, que es el unico caso en que seguro que la
     * funcion no llego a ejecutarse. Un 502/504 puede ser un guardado que
     * termino tarde: repetirlo creaba el plato dos veces (mejora 59 de
     * Alex, 6 sep 2026). Lecturas: cualquier 5xx o corte de la pasarela.
     */
    const reintentable = esLectura(init)
      ? (await esFalloDePasarela(response)) || response.status >= 500
      : response.status === 429;
    if (!reintentable) break;
    await new Promise((r) => setTimeout(r, espera + Math.random() * 300));
    response = await doFetch(path, init);
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError('INTERNAL', 'La respuesta del servidor no es válida.', response.status);
  }
}

/** Desenvuelve `{ items: [...] }` tal y como lo define el contrato. */
async function list<T>(path: string): Promise<T[]> {
  const data = await request<{ items?: T[] } | T[]>(path);
  if (Array.isArray(data)) return data;
  return data.items ?? [];
}

const body = (payload: unknown): RequestInit => ({ body: JSON.stringify(payload) });

function periodQuery(period: Period, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams({ start: period.start, end: period.end, ...extra });
  return `?${params.toString()}`;
}

const locationExtra = (locationId?: string): Record<string, string> =>
  locationId ? { locationId } : {};

/** Solo el local, para rutas sin periodo (`/v1/targets/2026-03?locationId=`). */
const locationOnlyQuery = (locationId?: string): string =>
  locationId ? `?${new URLSearchParams({ locationId }).toString()}` : '';

/**
 * La conversacion no puede romperse porque falte un campo: si el asistente
 * responde sin `toolCalls` o sin texto, la pantalla sigue teniendo algo
 * coherente que enseñar.
 */
function normalizeReply(data: Partial<AssistantChatResponse> | null): AssistantChatResponse {
  const rawCalls = Array.isArray(data?.toolCalls) ? data.toolCalls : [];
  const toolCalls: AssistantToolCall[] = rawCalls
    .filter((call): call is AssistantToolCall => Boolean(call) && typeof call === 'object')
    .map((call) => ({
      name: typeof call.name === 'string' ? call.name : 'consulta',
      input: call.input,
      summary:
        typeof call.summary === 'string' && call.summary.trim() !== ''
          ? call.summary
          : 'Consulta a tus datos',
    }));

  return {
    reply: typeof data?.reply === 'string' ? data.reply : '',
    toolCalls,
    usage: data?.usage,
  };
}

export const httpApi: AlexiaRestApi = {
  adminGrowthTarget: () => request('/v1/admin/growth-target'),
  adminSaveGrowthTarget: monthly => request('/v1/admin/growth-target', { method: 'PUT', ...body({ monthly }) }),
  trialExtension: () => request('/v1/me/trial-extension'),
  acceptTrialExtension: (token) => request('/v1/me/trial-extension/accept', { method:'POST', body:JSON.stringify({token}) }),
  adminTrialExtension: (tenantId) => request(`/v1/admin/trial-extension/${encodeURIComponent(tenantId)}`),
  adminOfferTrialExtension: (tenantId) => request(`/v1/admin/trial-extension/${encodeURIComponent(tenantId)}/offer`, {method:'POST'}),
  welcomeEmailPreference: () => request('/v1/me/welcome-emails'),
  setWelcomeEmailPreference: (enabled) => request('/v1/me/welcome-emails', { method: 'PUT', ...body({ enabled }) }),
  markWelcomeResultViewed: () => request('/v1/me/welcome-emails/result-viewed', { method: 'POST' }),
  adminWelcomeHistory: (tenantId) => request(`/v1/admin/welcome-emails/${encodeURIComponent(tenantId)}`),
  weeklyReportPreference: () => request('/v1/me/weekly-report'),
  setWeeklyReportPreference: (enabled) => request('/v1/me/weekly-report', { method: 'PUT', body: JSON.stringify({ enabled }) }),
  getPushSettings: () => request('/v1/push'),
  savePushSubscription: (subscription, remove) => request('/v1/push', { method: remove ? 'DELETE' : 'POST', ...body(subscription) }),
  getInboundEmailSettings: () => request('/v1/inbound-email/settings'),
  setInboundEmailAddress: (slug) => request('/v1/inbound-email/address', { method: 'POST', ...body({ slug }) }),
  setInboundEmailSenders: (emails) => request('/v1/inbound-email/senders', { method: 'PUT', ...body({ emails }) }),
  getQuestions: () => request('/v1/questions'),
  skipQuestion: async (id) => { await request(`/v1/questions/${encodeURIComponent(id)}/skip`, { method: 'POST', ...body({}) }); },
  answerQuestion: (id, input) => request(`/v1/questions/${encodeURIComponent(id)}/answer`, { method: 'POST', ...body(input) }),
  getWhatsAppSettings: () => request('/v1/whatsapp/settings'),
  requestWhatsAppCode: (phone, timezone) => request('/v1/whatsapp/link/request', { method: 'POST', ...body({ phone, timezone, locale: navigator.language }) }),
  verifyWhatsAppCode: (code) => request('/v1/whatsapp/link/verify', { method: 'POST', ...body({ code }) }),
  unlinkWhatsApp: () => request('/v1/whatsapp/unlink', { method: 'POST', ...body({}) }),
  updateWhatsAppPreferences: (input) => request('/v1/whatsapp/preferences', { method: 'PUT', ...body(input) }),
  getMe: () => request<MeResponse>('/v1/me'),
  onboard: (input) =>
    request<OnboardResponse>('/v1/onboarding', { method: 'POST', ...body(input) }),
  createPhotoUploadUrl: (contentType: string) =>
    request<PhotoUploadTicket>('/v1/photos/upload-url', {
      method: 'POST',
      ...body({ contentType }),
    }),
  getScanQuota: () => request<ScanQuotaResponse>('/v1/receipts/quota'),
  createScanUploadUrl: (contentType: string) =>
    request<PhotoUploadTicket>('/v1/receipts/upload-url', {
      method: 'POST',
      ...body({ contentType }),
    }),
  scanReceipt: (key: string, documentType?: PurchaseDocumentType) =>
    request<ScanResult>('/v1/receipts/scan', { method: 'POST', ...body({ key, documentType }) }),
  scanInventorySheet: (key: string) =>
    request<InventoryScanResult>('/v1/receipts/scan-inventory', { method: 'POST', ...body({ key }) }),
  getReceiptUrl: async (key: string) => {
    const data = await request<{ url: string }>(
      `/v1/receipts/url?key=${encodeURIComponent(key)}`,
    );
    return data.url;
  },
  getPhotoUrl: async (key: string) => {
    const data = await request<{ url: string }>(
      `/v1/photos/url?key=${encodeURIComponent(key)}`,
    );
    return data.url;
  },
  markTourSeen: () => request<{ tourSeenAt: string }>('/v1/me/tour', { method: 'POST' }),
  updateTenant: (patch: TenantUpdate) =>
    request<Tenant>('/v1/tenants/current', { method: 'PUT', ...body(patch) }),
  listLocations: () => list<Location>('/v1/locations'),
  createLocation: (name: string) =>
    request<Location>('/v1/locations', { method: 'POST', ...body({ name }) }),
  updateLocation: (locationId: string, input: { name: string; active: boolean }) =>
    request<Location>(`/v1/locations/${encodeURIComponent(locationId)}`, {
      method: 'PUT',
      ...body(input),
    }),

  exportBusiness: () => request<Record<string, object[]>>('/v1/settings/export'),
  getSettings: () => request<TenantSettings>('/v1/settings'),
  updateSettings: (input: TenantSettings) =>
    request<TenantSettings>('/v1/settings', { method: 'PUT', ...body(input) }),

  listPosIntegrations: () => request<PosIntegrationsResponse>('/v1/integrations/pos'),
  startPosConnection: (provider: PosProvider) =>
    request<{ url: string }>(`/v1/integrations/pos/${provider}/connect`, { method: 'POST' }),
  completePosConnection: (provider: PosProvider, input: { code: string; state: string }) =>
    request<PosIntegrationView>(`/v1/integrations/pos/${provider}/callback`, { method: 'POST', ...body(input) }),
  connectLoyverseToken: (token: string) =>
    request<PosIntegrationView>('/v1/integrations/pos/loyverse/token', { method: 'POST', ...body({ token }) }),
  connectGlopCredentials: (input: { clientId: string; clientSecret: string }) =>
    request<PosIntegrationView>('/v1/integrations/pos/glop/credentials', { method: 'POST', ...body(input) }),
  updatePosIntegration: (provider: PosProvider, connectionId: string, patch: PosIntegrationPatch) =>
    request<PosIntegrationView>(`/v1/integrations/pos/${provider}/${connectionId}`, { method: 'PUT', ...body(patch) }),
  syncPosSales: (provider: PosProvider, connectionId: string, period: Period) =>
    request<PosSyncResult>(`/v1/integrations/pos/${provider}/${connectionId}/sync`, { method: 'POST', ...body(period) }),
  applyPosUnits: (provider: PosProvider, period: Period) =>
    request<PosUnitsResult>(`/v1/integrations/pos/${provider}/units`, { method: 'POST', ...body(period) }),
  disconnectPos: (provider: PosProvider, connectionId: string) =>
    request<void>(`/v1/integrations/pos/${provider}/${connectionId}`, { method: 'DELETE' }),

  listInbox: () => list<InboxEntry>('/v1/alexia/inbox'),
  sendInbox: (input) => request('/v1/alexia/inbox', { method: 'POST', body: JSON.stringify(input) }),
  listDrafts: (locationId) => list<Draft>(`/v1/drafts${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`),
  getDraft: (id) => request(`/v1/drafts/${encodeURIComponent(id)}`),
  createRecipeDraft: (payload) => request('/v1/drafts', { method: 'POST', body: JSON.stringify({ kind: 'recipe', payload, summary: payload.name }) }),
  confirmDraft: (id, patch = {}) => request(`/v1/drafts/${encodeURIComponent(id)}/confirm`, { method: 'POST', body: JSON.stringify(patch) }),
  createDraftLink: (id) => request(`/v1/drafts/${encodeURIComponent(id)}/link`, { method: 'POST', body: '{}' }),
  redeemDraftLink: (id, token) => request(`/v1/drafts/${encodeURIComponent(id)}/redeem-link`, { method: 'POST', body: JSON.stringify({ token }) }),
  discardDraft: (id) => request(`/v1/drafts/${encodeURIComponent(id)}/discard`, { method: 'POST', body: '{}' }),
  listPresentations: () => list<string>('/v1/ingredients/presentations'),
  createPresentation: (name: string) => request<{ name: string }>('/v1/ingredients/presentations', { method: 'POST', body: JSON.stringify({ name }) }),
  listIngredients: () => list<Ingredient>('/v1/ingredients'),
  estimateMissingPrices: () => request('/v1/catalog/estimate-missing', { method: 'POST' }),
  createIngredient: (input: IngredientInput) =>
    request<Ingredient>('/v1/ingredients', { method: 'POST', ...body(input) }),
  updateIngredient: (id: string, input: IngredientInput) =>
    request<Ingredient>(`/v1/ingredients/${id}`, { method: 'PUT', ...body(input) }),
  deleteIngredient: (id: string) =>
    request<void>(`/v1/ingredients/${id}`, { method: 'DELETE' }),

  listSuppliers: () => list<Supplier>('/v1/suppliers'),
  createSupplier: (input: SupplierInput) =>
    request<Supplier>('/v1/suppliers', { method: 'POST', ...body(input) }),
  updateSupplier: (id: string, input: SupplierInput) =>
    request<Supplier>(`/v1/suppliers/${id}`, { method: 'PUT', ...body(input) }),
  deleteSupplier: (id: string) => request<void>(`/v1/suppliers/${id}`, { method: 'DELETE' }),

  listRecipes: () => list<Recipe>('/v1/recipes'),
  getRecipe: (id: string) => request<Recipe>(`/v1/recipes/${id}`),
  createRecipe: (input: RecipeInput) =>
    request<Recipe>('/v1/recipes', { method: 'POST', ...body(input) }),
  updateRecipe: (id: string, input: RecipeInput) =>
    request<Recipe>(`/v1/recipes/${id}`, { method: 'PUT', ...body(input) }),
  deleteRecipe: (id: string) => request<void>(`/v1/recipes/${id}`, { method: 'DELETE' }),
  getRecipeCost: (id: string) => request<RecipeCost>(`/v1/recipes/${id}/cost`),
  // Una sola peticion para toda la tabla de recetas: ver el comentario del
  // endpoint en la API.
  getRecipeCosts: () => request<RecipeCostsResponse>('/v1/recipes/costs'),
  transcribeRecipeAudio: (input: RecipeAudioInput) =>
    request<{ text: string }>('/v1/recipes/transcribe', { method: 'POST', ...body(input) }),
  proposeRecipeFromFile: async (file: Blob) => {
    const contentType = file.type || 'application/pdf';
    const ticket = await request<PhotoUploadTicket>('/v1/recipes/input-upload-url', { method: 'POST', ...body({ contentType }) });
    const form = new FormData();
    for (const [key, value] of Object.entries(ticket.fields)) form.append(key, value);
    form.append('file', file);
    const uploaded = await fetch(ticket.uploadUrl, { method: 'POST', body: form });
    if (!uploaded.ok) throw new Error('No se ha podido subir la ficha. Inténtalo otra vez.');
    return request<RecipeProposalResponse>('/v1/alexia/recipe-proposal', { method: 'POST', ...body({ imageKey: ticket.key }) });
  },
  proposeRecipe: (text: string) =>
    request<RecipeProposalResponse>('/v1/recipes/proposal', { method: 'POST', ...body({ text }) }),
  confirmRecipeProposal: (input: ConfirmRecipeProposalInput) =>
    request<ConfirmRecipeProposalResponse>('/v1/recipes/proposal/confirm', { method: 'POST', ...body(input) }),

  listMenuItems: () => list<MenuItem>('/v1/menu-items'),
  createMenuItem: (input: MenuItemInput) =>
    request<MenuItem>('/v1/menu-items', { method: 'POST', ...body(input) }),
  updateMenuItem: (id: string, input: MenuItemInput) =>
    request<MenuItem>(`/v1/menu-items/${id}`, { method: 'PUT', ...body(input) }),
  updateMenuItemUnitsSold: (id: string, unitsSold: number, locationId?: string) =>
    request<MenuItem>(`/v1/menu-items/${id}/units-sold`, {
      method: 'PUT',
      ...body({ unitsSold, locationId }),
    }),
  deleteMenuItem: (id: string) => request<void>(`/v1/menu-items/${id}`, { method: 'DELETE' }),
  acceptMenuItemCost: (id: string) =>
    request<MenuItem>(`/v1/menu-items/${id}/accept-cost`, { method: 'POST' }),
  getPricing: (locationId?: string) =>
    request<PricingResponse>(`/v1/menu/pricing${locationOnlyQuery(locationId)}`),
  getPlatformComparison: (id: string) =>
    list<PricingAnalysis>(`/v1/menu-items/${id}/platforms`),
  getCostDeviations: () => list<CostDeviation>('/v1/menu/deviations'),

  listRevenues: (period: Period, locationId?: string) =>
    list<DailyRevenue>(`/v1/revenues${periodQuery(period, locationExtra(locationId))}`),
  saveRevenue: (input: DailyRevenueInput & { importSource?: 'excel' }) =>
    request<DailyRevenue>('/v1/revenues', { method: 'POST', ...body(input) }),
  deleteRevenue: (date: string, locationId?: string) =>
    request<void>(
      `/v1/revenues/${date}${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`,
      { method: 'DELETE' },
    ),

  listExpenses: (period: Period, locationId?: string) =>
    list<ExpenseEntry>(`/v1/expenses${periodQuery(period, locationExtra(locationId))}`),
  listReconciliationCandidates: (invoiceId: string, locationId?: string) =>
    list<ExpenseEntry>(
      `/v1/expenses/${encodeURIComponent(invoiceId)}/reconciliation-candidates${locationOnlyQuery(locationId)}`,
    ),
  getExpense: (id: string) => request<ExpenseEntry>(`/v1/expenses/${encodeURIComponent(id)}`),
  createExpense: (input: ExpenseInput) =>
    request<ExpenseEntry>('/v1/expenses', { method: 'POST', ...body(input) }),
  /** Importacion por archivo: hasta 2.000 gastos por llamada. */
  importExpenses: (items: ExpenseInput[]) =>
    request<{ created: number; skipped: number }>('/v1/expenses/bulk', { method: 'POST', ...body({ items }) }),
  updateExpense: (id: string, input: ExpenseInput) =>
    request<ExpenseEntry>(`/v1/expenses/${id}`, { method: 'PUT', ...body(input) }),
  deleteExpense: (id: string) => request<void>(`/v1/expenses/${id}`, { method: 'DELETE' }),
  reconcileExpense: (invoiceId: string, deliveryNoteIds: string[], marcarFacturaPagada?: boolean) =>
    request<ReconciliationResult>(`/v1/expenses/${encodeURIComponent(invoiceId)}/reconcile`, {
      method: 'POST',
      ...body({ deliveryNoteIds, marcarFacturaPagada }),
    }),
  unreconcileExpense: (invoiceId: string) =>
    request<{ invoiceId: string; deliveryNotes: number }>(`/v1/expenses/${encodeURIComponent(invoiceId)}/unreconcile`, {
      method: 'POST',
      ...body({}),
    }),
  getPayables: (today: string, locationId?: string) =>
    request<PayablesResponse>(
      `/v1/expenses/payables?today=${encodeURIComponent(today)}${locationId ? `&locationId=${encodeURIComponent(locationId)}` : ''}`,
    ),

  listInvestments: (locationId?: string) =>
    list<Investment>(`/v1/investments${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`),
  createInvestment: (input: InvestmentInput) =>
    request<Investment>('/v1/investments', { method: 'POST', ...body(input) }),
  updateInvestment: (id: string, input: InvestmentInput) =>
    request<Investment>(`/v1/investments/${id}`, { method: 'PUT', ...body(input) }),
  deleteInvestment: (id: string) =>
    request<void>(`/v1/investments/${id}`, { method: 'DELETE' }),
  getAmortizationSchedule: (month: string, locationId?: string) =>
    list<AmortizationRow>(
      `/v1/investments/schedule?month=${encodeURIComponent(month)}${locationId ? `&locationId=${encodeURIComponent(locationId)}` : ''}`,
    ),

  listCashMovements: (period: Period, locationId?: string) =>
    list<CashMovement>(`/v1/cash-movements${periodQuery(period, locationExtra(locationId))}`),
  createCashMovement: (input: CashMovementInput) =>
    request<CashMovement>('/v1/cash-movements', { method: 'POST', ...body(input) }),
  deleteCashMovement: (id: string) =>
    request<void>(`/v1/cash-movements/${id}`, { method: 'DELETE' }),

  getTarget: (month: string, locationId?: string) =>
    request<Target | null>(`/v1/targets/${month}${locationOnlyQuery(locationId)}`),
  saveTarget: (month: string, input: TargetInput) =>
    request<Target>(`/v1/targets/${month}`, { method: 'PUT', ...body(input) }),

  listInventoryCounts: () => list<InventoryCount>('/v1/inventory'),
  getInventoryCount: (month: string, locationId?: string) =>
    request<InventoryCount>(
      `/v1/inventory/${month}${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`,
    ),
  saveInventoryCount: (month: string, input: InventoryCountInput) =>
    request<InventoryCount>(`/v1/inventory/${month}`, { method: 'PUT', ...body(input) }),
  deleteInventoryCount: (month: string, locationId?: string) =>
    request<void>(
      `/v1/inventory/${month}${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`,
      { method: 'DELETE' },
    ),

  getPnl: (period: Period, locationId?: string) =>
    request<ProfitAndLoss>(`/v1/analytics/pnl${periodQuery(period, locationExtra(locationId))}`),
  // OJO: la API envuelve las colecciones en { items }. Con `request` en vez de
  // `list` llegaba un objeto donde la pantalla esperaba un array y la Cuenta
  // de resultados se quedaba EN BLANCO (el fallo era de render, no de red, asi
  // que QueryBoundary no lo veia). En demo no pasaba: el adaptador devuelve el
  // array de verdad. Lo reporto Alex el 12 de agosto.
  getMonthlyPnl: (period: Period, locationId?: string) =>
    list<ProfitAndLoss>(`/v1/analytics/pnl/monthly${periodQuery(period, locationExtra(locationId))}`),
  getCashFlow: (period: Period, opening = 0, locationId?: string) =>
    request<CashFlowResponse>(
      `/v1/analytics/cashflow${periodQuery(period, { opening: String(opening), ...locationExtra(locationId) })}`,
    ),
  getKpis: (period: Period, locationId?: string) =>
    request<KpisResponse>(`/v1/analytics/kpis${periodQuery(period, locationExtra(locationId))}`),
  getWeekday: (period: Period, locationId?: string) =>
    list<WeekdayPerformance>(`/v1/analytics/weekday${periodQuery(period, locationExtra(locationId))}`),
  getTargetComparison: (month: string, locationId?: string) =>
    list<TargetComparison>(`/v1/analytics/targets/${month}${locationOnlyQuery(locationId)}`),
  allocateTax: async (input: TaxAllocationRequest) => {
    const data = await request<{ items?: TaxAllocation[] } | TaxAllocation[]>(
      '/v1/analytics/tax-allocation',
      { method: 'POST', ...body(input) },
    );
    return Array.isArray(data) ? data : (data.items ?? []);
  },
  getDashboard: (period: Period, locationId?: string) =>
    request<DashboardResponse>(`/v1/analytics/dashboard${periodQuery(period, locationExtra(locationId))}`),

  chatWithAssistant: async (input: AssistantChatRequest) => {
    const data = await request<Partial<AssistantChatResponse>>('/v1/assistant/chat', {
      method: 'POST',
      ...body(input),
    });
    return normalizeReply(data);
  },
  proponerMapeoVentas: (input: {
    columnas: string[];
    muestra: string[][];
    destinos: Array<{ key: string; label: string }>;
    tipo?: 'ventas' | 'gastos';
  }) =>
    request<MapeoVentasPropuesta>('/v1/assistant/mapeo-ventas', { method: 'POST', ...body(input) }),
  getAssistantSuggestions: (period: Period, locationId?: string) =>
    list<AssistantSuggestion>(`/v1/assistant/suggestions${periodQuery(period, locationExtra(locationId))}`),

  getInvoices: () => list<Invoice>('/v1/billing/invoices'),
  /*
   * El PDF va detras del token, asi que no vale un <a href>: se pide con la
   * cabecera puesta y se abre el blob. Pasa por nuestra API a proposito, para
   * que la clave de Holded no salga nunca del servidor.
   */
  getInvoicePdfUrl: async (id: string): Promise<string> => {
    const path = `/v1/billing/invoices/${id}/pdf`;
    let response = await doFetch(path, {});
    // Misma renovacion de sesion que `request`: un token caducado no puede
    // convertir «descargar factura» en «vuelve a iniciar sesion».
    if (response.status === 401 && (await refreshSession())) response = await doFetch(path, {});
    if (!response.ok) throw await toApiError(response);
    return URL.createObjectURL(await response.blob());
  },

  startCheckout: (plan: 'basic' | 'pro', interval: BillingInterval = 'month') =>
    // La oferta por invitación viaja con el checkout por si el alta fue anterior al enlace.
    request<{ url: string }>('/v1/billing/checkout', { method: 'POST', ...body({ plan, interval, invitation: storedInvitation() }) }),
  startFounderCheckout: (code: string) =>
    request<{ url: string }>('/v1/billing/founder-checkout', {
      method: 'POST',
      ...body({ code }),
    }),
  changePlan: (plan: 'basic' | 'pro', interval?: BillingInterval) =>
    request<{ ok: true }>('/v1/billing/change-plan', {
      method: 'POST',
      ...body(interval ? { plan, interval } : { plan }),
    }),
  contractAddon: (addon: 'escaner') =>
    request<{ ok: true }>('/v1/billing/addons', { method: 'POST', ...body({ addon }) }),
  cancelAddon: (addon: 'escaner') =>
    request<{ ok: true }>(`/v1/billing/addons/${addon}`, { method: 'DELETE' }),
  saveCancellationFeedback: (reason) => request('/v1/billing/cancellation-feedback', { method: 'POST', ...body({ reason }) }),
  openBillingPortal: () =>
    request<{ url: string }>('/v1/billing/portal', { method: 'POST' }),
  setExtraSeats: (quantity: number) =>
    request<{ ok: true }>('/v1/billing/seats', { method: 'POST', ...body({ quantity }) }),
  setExtraLocations: (quantity: number) =>
    request<{ ok: true }>('/v1/billing/locations', { method: 'POST', ...body({ quantity }) }),

  getTeam: () => request<TeamResponse>('/v1/team'),
  inviteMember: (input: InviteInput) =>
    request<{ membership: Membership }>('/v1/team/invitations', {
      method: 'POST',
      ...body(input),
    }),
  changeMemberRole: (userId: string, role: AssignableRole, customRoleId?: string) =>
    request<{ membership: Membership }>(`/v1/team/${encodeURIComponent(userId)}/role`, {
      method: 'PUT',
      ...body({ role, customRoleId }),
    }),
  listCustomRoles: () => request<{ roles: CustomRole[] }>('/v1/team/roles'),
  createCustomRole: (input: CustomRoleInput) =>
    request<{ role: CustomRole }>('/v1/team/roles', { method: 'POST', ...body(input) }),
  updateCustomRole: (roleId: string, input: CustomRoleInput) =>
    request<{ role: CustomRole }>(`/v1/team/roles/${encodeURIComponent(roleId)}`, {
      method: 'PUT',
      ...body(input),
    }),
  deleteCustomRole: (roleId: string) =>
    request<{ ok: true }>(`/v1/team/roles/${encodeURIComponent(roleId)}`, { method: 'DELETE' }),
  deactivateMember: (userId: string) =>
    request<{ membership: Membership }>(`/v1/team/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),
  activateMember: (userId: string) =>
    request<{ membership: Membership }>(`/v1/team/${encodeURIComponent(userId)}/activate`, {
      method: 'POST',
    }),
  resendInvitation: (userId: string) =>
    request<{ ok: true; membership: Membership; aviso?: string }>(
      `/v1/team/${encodeURIComponent(userId)}/invitations/resend`,
      { method: 'POST' },
    ),

  /** `fresh` salta la cache del servidor: el boton «Actualizar» del panel. */
  getAdminOverview: (fresh = false) => request<AdminOverview>(`/v1/admin/overview${fresh ? '?fresh=1' : ''}`),
  adminChangePlan: (tenantId: string, plan: 'basic' | 'pro') =>
    request<{ ok: true }>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/plan`, {
      method: 'POST',
      ...body({ plan }),
    }),
  /** Manda ahora mismo el resumen semanal del lunes a los correos de avisos. */
  adminEnviarResumenSemanal: () =>
    request<{ enviado: boolean; destinatarios: string[]; asunto: string }>('/v1/admin/resumen-semanal/enviar', {
      method: 'POST',
    }),
  adminRetryHolded: (tenantId: string) =>
    request<{ resultados: HoldedReintento[] }>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/holded/reintentar`, {
      method: 'POST',
    }),
  adminSetInternal: (tenantId: string, internal: boolean) =>
    request<{ ok: true; internal: boolean }>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/internal`, {
      method: 'POST',
      ...body({ internal }),
    }),
  adminSetBonusLocations: (tenantId: string, bonusLocations: number) =>
    request<{ ok: true; bonusLocations: number }>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/bonus-locations`, {
      method: 'POST',
      ...body({ bonusLocations }),
    }),
  adminApplyCoupon: (tenantId: string, coupon: string) =>
    request<{ ok: true }>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/coupon`, {
      method: 'POST',
      ...body({ coupon }),
    }),
  getPlatformPnl: (year: number) =>
    request<PlatformPnlResponse>(`/v1/admin/pnl?year=${year}`),
  createPlatformExpense: (input: PlatformExpenseInput) =>
    request<PlatformExpense>('/v1/admin/expenses', { method: 'POST', ...body(input) }),
  createPlatformRecurring: (input) =>
    request<PlatformRecurringExpense>('/v1/admin/recurring', { method: 'POST', ...body(input) }),
  deletePlatformRecurring: (recurringId) =>
    request<void>(`/v1/admin/recurring/${encodeURIComponent(recurringId)}`, { method: 'DELETE' }),
  setPlatformSettings: (input) =>
    request<PlatformSettings>('/v1/admin/settings', { method: 'PUT', ...body(input) }),
  setPlatformSettlement: (month, input) =>
    request<unknown>(`/v1/admin/settlements/${encodeURIComponent(month)}`, { method: 'PUT', ...body(input) }),
  deletePlatformExpense: (month: string, expenseId: string) =>
    request<void>(
      `/v1/admin/expenses/${encodeURIComponent(month)}/${encodeURIComponent(expenseId)}`,
      { method: 'DELETE' },
    ),

  importIngredients: (batch: IngredientBatch) =>
    request<BatchResult>('/v1/import/ingredients', { method: 'POST', ...body(batch) }),
  importRecipes: (batch: RecipeBatch) =>
    request<BatchResult>('/v1/import/recipes', { method: 'POST', ...body(batch) }),
};

/** Punto de entrada unico: HTTP real o demo en memoria. */
export const api: AlexiaRestApi = isDemoMode ? demoApi : httpApi;

export { ApiError } from './error';
export type { AlexiaRestApi } from './contract';
